import { useCallback, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

const FBC_STORAGE_KEY = "ppp_fbc";
const FBC_TIMESTAMP_KEY = "ppp_fbc_timestamp";
const FBCLID_STORAGE_KEY = "ppp_fbclid";
const LEAD_STORAGE_KEY = "ppp_lead_data";

export interface StoredLeadData {
  firstName: string;
  email: string;
  phone: string;
  quizAnswers: {
    hasBumps: string;
    duration: string;
    quantity: string;
    impact: string;
    previousAttempts: string;
    interest: string;
  };
}

// Get stored lead data for event enrichment
export const getStoredLeadData = (): StoredLeadData | null => {
  const stored = localStorage.getItem(LEAD_STORAGE_KEY);
  return stored ? JSON.parse(stored) : null;
};

// Generate or retrieve persistent external_id
export const getExternalId = (): string => {
  const storageKey = "ppp_external_id";
  let externalId = localStorage.getItem(storageKey);
  if (!externalId) {
    externalId = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem(storageKey, externalId);
  }
  return externalId;
};

// Get _fbp cookie value
export const getFbp = (): string | null => {
  const match = document.cookie.match(/_fbp=([^;]+)/);
  return match?.[1] ?? null;
};

// Enhanced fbc retrieval with multiple fallback sources
export const getFbc = (): string | null => {
  // 1. Try cookie first (most reliable when available)
  const cookieMatch = document.cookie.match(/_fbc=([^;]+)/);
  if (cookieMatch && cookieMatch[1]) {
    // Also update localStorage backup
    localStorage.setItem(FBC_STORAGE_KEY, cookieMatch[1]);
    return cookieMatch[1];
  }
  
  // 2. Fallback to localStorage (persists even if cookies blocked)
  const storedFbc = localStorage.getItem(FBC_STORAGE_KEY);
  const storedTimestamp = localStorage.getItem(FBC_TIMESTAMP_KEY);
  
  if (storedFbc && storedTimestamp) {
    const timestamp = parseInt(storedTimestamp, 10);
    const ninetyDaysMs = 90 * 24 * 60 * 60 * 1000;
    
    // Check if fbc is still valid (within 90 days)
    if (Date.now() - timestamp < ninetyDaysMs) {
      return storedFbc;
    }
  }
  
  // 3. Try to construct fbc from stored fbclid
  const storedFbclid = localStorage.getItem(FBCLID_STORAGE_KEY);
  if (storedFbclid && storedTimestamp) {
    const timestamp = parseInt(storedTimestamp, 10);
    const ninetyDaysMs = 90 * 24 * 60 * 60 * 1000;
    
    if (Date.now() - timestamp < ninetyDaysMs) {
      const constructedFbc = `fb.1.${timestamp}.${storedFbclid}`;
      return constructedFbc;
    }
  }
  
  return null;
};

// Enhanced fbclid capture with multiple sources and storage strategies
export const captureFbclid = () => {
  const now = Date.now();
  let fbclid: string | null = null;
  
  // 1. Check URL parameters (primary source)
  const urlParams = new URLSearchParams(window.location.search);
  fbclid = urlParams.get("fbclid");
  
  // 2. Check URL hash (some redirects put it there)
  if (!fbclid && window.location.hash) {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    fbclid = hashParams.get("fbclid");
  }
  
  // 3. Check referrer URL for fbclid
  if (!fbclid && document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer);
      fbclid = referrerUrl.searchParams.get("fbclid");
    } catch {
      // Invalid referrer URL, ignore
    }
  }
  
  // 4. Check if fbclid exists in the full URL (some tracking systems encode it differently)
  if (!fbclid) {
    const fullUrl = window.location.href;
    const fbclidMatch = fullUrl.match(/fbclid=([^&\s#]+)/);
    if (fbclidMatch) {
      fbclid = fbclidMatch[1] ?? null;
    }
  }
  
  if (fbclid) {
    const fbc = `fb.1.${now}.${fbclid}`;
    
    // Store in cookie with proper attributes for cross-subdomain support
    const expires = new Date();
    expires.setTime(expires.getTime() + 90 * 24 * 60 * 60 * 1000); // 90 days
    
    // Set cookie with multiple attribute combinations for maximum compatibility
    const cookieBase = `_fbc=${fbc}; expires=${expires.toUTCString()}; path=/`;
    
    // Try with SameSite=Lax (most compatible)
    document.cookie = `${cookieBase}; SameSite=Lax`;
    
    // Also set with Secure flag for HTTPS
    if (window.location.protocol === 'https:') {
      document.cookie = `${cookieBase}; SameSite=None; Secure`;
    }
    
    // Store in localStorage as backup (survives cookie blocking)
    localStorage.setItem(FBC_STORAGE_KEY, fbc);
    localStorage.setItem(FBC_TIMESTAMP_KEY, now.toString());
    localStorage.setItem(FBCLID_STORAGE_KEY, fbclid);
    
    // Store in sessionStorage for current session (extra backup)
    sessionStorage.setItem(FBC_STORAGE_KEY, fbc);
    
    console.log("[FBC] Captured and stored fbclid:", fbclid.substring(0, 10) + "...");
  } else {
    // Even without new fbclid, ensure we sync cookie to localStorage
    const cookieMatch = document.cookie.match(/_fbc=([^;]+)/);
    if (cookieMatch && cookieMatch[1]) {
      localStorage.setItem(FBC_STORAGE_KEY, cookieMatch[1]);
      console.log("[FBC] Synced existing cookie to localStorage");
    }
  }
};

// Initialize fbc capture on every page - call this early
export const initializeFbcTracking = () => {
  // Capture fbclid immediately
  captureFbclid();
  
  // Also capture on any URL changes (SPA navigation)
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;
  
  history.pushState = function(...args) {
    originalPushState.apply(history, args);
    setTimeout(captureFbclid, 100);
  };
  
  history.replaceState = function(...args) {
    originalReplaceState.apply(history, args);
    setTimeout(captureFbclid, 100);
  };
  
  // Capture on popstate (back/forward navigation)
  window.addEventListener('popstate', () => {
    setTimeout(captureFbclid, 100);
  });
};

// Section names for scroll tracking
export type SectionName = 
  | "hero" 
  | "method1" 
  | "method2" 
  | "method3" 
  | "before_after" 
  | "tips" 
  | "warnings" 
  | "cta";

interface UseMetaPixelReturn {
  trackPageView: () => void;
  trackSectionView: (section: SectionName) => void;
  trackCTAVisible: () => void;
  trackCTAClick: (buttonLocation: string) => void;
  trackInitiateCheckout: (buttonLocation: string) => void;
  trackLead: (leadData: StoredLeadData) => void;
  trackQuizStart: () => void;
  trackQuizStep: (step: number, answer: string) => void;
  trackQuizComplete: (quizAnswers: Record<string, string>) => void;
  trackCompleteRegistration: (leadData: StoredLeadData) => void;
}

export const useMetaPixel = (): UseMetaPixelReturn => {
  const sectionsFiredRef = useRef<Set<SectionName>>(new Set());
  const ctaVisibleFiredRef = useRef(false);
  const pageViewFiredRef = useRef(false);

  // Send event to server (CAPI) - visitor upsert moved to edge function
  const sendToServer = useCallback(async (eventName: string, customData?: Record<string, unknown>) => {
    const externalId = getExternalId();
    const fbp = getFbp();
    const fbc = getFbc();
    const eventSourceUrl = window.location.href;
    
    // Get lead data for enrichment if available
    const leadData = getStoredLeadData();

    try {
      // Send to CAPI edge function (visitor upsert happens server-side with service_role)
      await supabase.functions.invoke("meta-capi", {
        body: {
          event_name: eventName,
          external_id: externalId,
          fbp,
          fbc,
          event_source_url: eventSourceUrl,
          custom_data: customData,
          // Include lead data for user matching enrichment
          user_data: leadData ? {
            em: leadData.email,
            ph: leadData.phone,
            fn: leadData.firstName,
          } : undefined,
        },
      });

      console.log(`[CAPI] ${eventName} sent`);
    } catch (error) {
      console.error(`[CAPI] Error sending ${eventName}:`, error);
    }
  }, []);

  // Track Pixel event (browser-side)
  const trackPixel = useCallback((eventName: string, customData?: Record<string, unknown>, isCustom = false) => {
    if (typeof window !== "undefined" && (window as any).fbq) {
      const externalId = getExternalId();
      const eventData = {
        ...customData,
        external_id: externalId,
      };

      if (isCustom) {
        (window as any).fbq("trackCustom", eventName, eventData);
      } else {
        (window as any).fbq("track", eventName, eventData);
      }
      console.log(`[Pixel] ${eventName} tracked`, eventData);
    }
  }, []);

  // Combined tracking (Pixel + CAPI)
  const trackEvent = useCallback((eventName: string, customData?: Record<string, unknown>, isCustom = false) => {
    trackPixel(eventName, customData, isCustom);
    sendToServer(eventName, customData);
  }, [trackPixel, sendToServer]);

  // PageView
  const trackPageView = useCallback(() => {
    if (pageViewFiredRef.current) return;
    pageViewFiredRef.current = true;
    trackEvent("PageView");
  }, [trackEvent]);

  // Section View (scroll progress)
  const trackSectionView = useCallback((section: SectionName) => {
    if (sectionsFiredRef.current.has(section)) return;
    sectionsFiredRef.current.add(section);
    trackEvent(`Section_${section}`, { section_name: section }, true);
  }, [trackEvent]);

  // CTA Visible
  const trackCTAVisible = useCallback(() => {
    if (ctaVisibleFiredRef.current) return;
    ctaVisibleFiredRef.current = true;
    trackEvent("CTA_Visible", { content_name: "CTA Section" }, true);
  }, [trackEvent]);

  // CTA Click
  const trackCTAClick = useCallback((buttonLocation: string) => {
    trackEvent("CTA_Click", { button_location: buttonLocation }, true);
  }, [trackEvent]);

  // InitiateCheckout
  const trackInitiateCheckout = useCallback((buttonLocation: string) => {
    trackEvent("InitiateCheckout", {
      value: 14.90,
      currency: "USD",
      button_location: buttonLocation,
    });
  }, [trackEvent]);

  // Lead event - for qualified leads who completed the quiz
  const trackLead = useCallback((leadData: StoredLeadData) => {
    trackEvent("Lead", {
      content_name: "Quiz Completed",
      content_category: "Lead Capture",
      quiz_answers: leadData.quizAnswers,
    });
  }, [trackEvent]);

  // Quiz Start - when user begins the quiz
  const trackQuizStart = useCallback(() => {
    trackEvent("Quiz_Start", { content_name: "Lead Quiz" }, true);
  }, [trackEvent]);

  // Quiz Step - track each question answered (separate events for each step)
  const trackQuizStep = useCallback((step: number, answer: string) => {
    trackEvent(`Quiz_Step_${step}`, { step_number: step, answer }, true);
  }, [trackEvent]);

  // Quiz Complete - when all questions are answered
  const trackQuizComplete = useCallback((quizAnswers: Record<string, string>) => {
    trackEvent("Quiz_Complete", {
      content_name: "Lead Quiz",
      ...quizAnswers,
    }, true);
  }, [trackEvent]);

  // CompleteRegistration - when lead form is submitted
  const trackCompleteRegistration = useCallback((leadData: StoredLeadData) => {
    trackEvent("CompleteRegistration", {
      content_name: "Lead Form",
      status: "qualified",
      has_bumps: leadData.quizAnswers.hasBumps,
      interest_level: leadData.quizAnswers.interest,
    });
  }, [trackEvent]);

  return {
    trackPageView,
    trackSectionView,
    trackCTAVisible,
    trackCTAClick,
    trackInitiateCheckout,
    trackLead,
    trackQuizStart,
    trackQuizStep,
    trackQuizComplete,
    trackCompleteRegistration,
  };
};

// Hook for section visibility tracking
export const useSectionTracking = (sectionRef: React.RefObject<HTMLElement>, sectionName: SectionName) => {
  const { trackSectionView } = useMetaPixel();
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTrackedRef.current) {
            hasTrackedRef.current = true;
            trackSectionView(sectionName);
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [sectionRef, sectionName, trackSectionView]);
};
